import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import syncApi from 'ecommerce/api/sync';
import { Grid, Card, Typography, Spinner } from 'components/base';
import { formatDateTime } from 'utils/format';
import uuid from 'utils/uuid';
import Icon, { ICON_TYPE } from 'components/icon';
import { useRouter } from 'next/router';
import useModalContext from 'components/context/modal';
import styled from 'styled-components';
import useMyCreations from 'ecommerce/hooks/useMyCreations';
import { animateScroll } from 'react-scroll';
import uniq from 'utils/uniq';
import dataApi from 'data/api/data';

import useEditorRouter from '../../lib/editorRouter';
import useEditorContext from '../../context/editorContext';
import { Button } from '../../components/base';
import Page from '../../components/page/preview';
import { getOriginalPage } from 'editor-utils/template';
import { parseData } from 'editor-utils/dataParser';
import EditorLayout from './layout';

const NewCreationButton = styled(Button)`
  height: auto;
  width: auto;
  padding: 2rem 3rem;

  svg {
    transition: 0.2s linear color;
    margin-top: 1rem;
  }

  &:hover {
    svg {
      color: ${props => props.theme.colors.primary} !important;
    }
  }
`;

const CardContent = styled.div`
  margin: 1rem;
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: space-between;
`;

const versionedTemplate = (template, version) => {
  return {
    ...template,
    version,
    assetRootUrl: template.assetRootUrl.replace(`/${template.version}`, `/${version}`),
  };
};

const templateCacheKey = (id, version) => `${id}#${version}`;

const DraftList = props => {
  const [numberOfVisibleDrafts, setNumberOfVisibleDrafts] = useState(11);
  const editorRouter = useEditorRouter(props.product.id);
  const editorContext = useEditorContext();
  const modalContext = useModalContext();
  const router = useRouter();

  const [templates, setTemplates] = useState({
    [templateCacheKey(props.template.id, props.template.version)]: props.template,
  });

  const myCreations = useMyCreations(editorContext.accessToken, props.productIds, item => {
    if (item.productId === props.product.id) {
      const template = versionedTemplate(props.template, item.data.templateVersion);
      const { pages, photos } = parseData(template, item.data.data, {
        onlyFirstPage: true,
      });

      if (pages.length > 0) {
        return {
          ...item,
          previewPage: pages[0],
          photos,
        };
      }
    }
    return null;
  });

  const templateKeys = useMemo(() => {
    if (myCreations.data) {
      return uniq(
        myCreations.data.map(item => templateCacheKey(item.productId, item.data.templateVersion)),
      );
    }
    return [];
  }, [myCreations.data]);

  useEffect(() => {
    if (templateKeys.length > 0) {
      (async () => {
        const { data } = await dataApi.getVersionedTemplates(
          templateKeys.map(i => {
            const [productId, version] = i.split('#');
            return { productId, version };
          }),
        );
        if (data) {
          setTemplates(val => ({
            ...val,
            ...data.reduce((acc, item) => {
              acc[templateCacheKey(item.id, item.version)] = item;
              return acc;
            }, {}),
          }));
        }
      })();
    }
  }, [templateKeys]);

  const handleOnLeaveClick = e => {
    e.preventDefault();

    router.push('/[...params]', props.product.url);
  };

  const handleOnContinueDraftClick = async (e, draftId) => {
    e.preventDefault();

    const { data: draft, error } = await syncApi(editorContext.accessToken).getProductDraft(
      draftId,
    );

    if (error) {
      modalContext.toast('Oops, something went wrong.', { type: 'warning' });
      console.error(error);
      return;
    }

    const version = draft.data.templateVersion;
    const draftName = draft.data.draftName;
    const draftLastModified = draft.updated_at;
    const data = draft.data.data;

    const template = versionedTemplate(props.template, version);

    const itemId = uuid();
    editorContext.editDraft(
      itemId,
      props.product.id,
      draftId,
      draftName,
      draftLastModified,
      template,
      data,
      props.item.bundle,
    );
    editorRouter.push('/preview', { id: itemId, version }, false);
  };

  const handleOnDuplicateDraftClick = async (e, draftId) => {
    e.preventDefault();

    const { error } = await syncApi(editorContext.accessToken).duplicateProductDraft(draftId);

    if (error) {
      modalContext.toast('Oops, something went wrong.', { type: 'warning' });
      console.error(error);
      return;
    }

    await myCreations.reload();
    setNumberOfVisibleDrafts(11);
    animateScroll.scrollTo(0, {
      containerId: 'editor-container',
      smooth: true,
    });
  };

  const handleOnRemoveDraftClick = async (e, draftId) => {
    e.preventDefault();

    modalContext.confirm('Do you really want to remove your creation?', {
      title: 'Remove creation?',
      onConfirm: async () => {
        await myCreations.deleteCreation(draftId);
      },
    });
  };

  if (myCreations.isEmpty) {
    return props.children;
  }

  return (
    <EditorLayout
      actionButton={
        <Button variant="secondary" onClick={handleOnLeaveClick}>
          Go Back
        </Button>
      }
      showPrice={false}
    >
      <div className="mw-1280 mx-auto">
        {myCreations.isLoading ? (
          <div className="text-center w-100">
            <Spinner theme="primary" className="mb-3 mx-auto" />
            <Typography.P>Please wait, we are loading your creations ...</Typography.P>
          </div>
        ) : (
          <>
            <Grid.Row>
              <Grid.Col xs={12} className="text-center">
                <Typography.H3 className="mb-3">Continue with your creation</Typography.H3>
                <Typography.P>Pick up your project where you left off.</Typography.P>
              </Grid.Col>
              <Grid.Col xs={12} sm={6} md={4} lg={3} className="text-center mt-2">
                <Card className="mw-560 mx-auto text-center h-100 mb-0 bg-transparent border-0">
                  <div className="m-1 h-100 d-flex flex-direction-column align-items-center justify-content-center">
                    <NewCreationButton variant="primary" onClick={() => myCreations.clear()}>
                      Start a new
                      <br />
                      creation
                      <br />
                      <Icon type={ICON_TYPE.PLUS} className="text-white mx-auto" />
                    </NewCreationButton>
                  </div>
                </Card>
              </Grid.Col>
              {myCreations.data.slice(0, numberOfVisibleDrafts).map(draft => {
                const template =
                  templates[templateCacheKey(draft.data.productId, draft.data.templateVersion)];

                return (
                  <Grid.Col
                    key={draft.id}
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                    className="text-center mt-2"
                  >
                    <Card className="mw-560 mx-auto text-center h-100 mb-0">
                      <CardContent>
                        <Typography.H4 className="m-0 mb-1">
                          {draft.data.draftName || 'Unnamed creation'}
                        </Typography.H4>

                        {template && (
                          <Page
                            page={getOriginalPage(
                              template,
                              draft.previewPage,
                              template.cover ? 'cover' : 'pages',
                            )}
                            photos={draft.photos}
                            template={template}
                            fullWidth
                          />
                        )}

                        <div className="mt-1">
                          <Typography.P>
                            Last saved: <strong>{formatDateTime(draft.updated_at)}</strong>
                          </Typography.P>

                          <div className="mt-1 d-flex justify-content-space-evenly">
                            <Button
                              variant="secondary"
                              onClick={e => handleOnContinueDraftClick(e, draft.id)}
                            >
                              Resume
                            </Button>
                            <Button
                              variant="secondary"
                              onClick={e => handleOnDuplicateDraftClick(e, draft.id)}
                            >
                              Duplicate
                            </Button>
                            <button
                              className="text-primary"
                              onClick={e => handleOnRemoveDraftClick(e, draft.id)}
                            >
                              <Icon type={ICON_TYPE.TRASH} />
                            </button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Grid.Col>
                );
              })}
            </Grid.Row>
            {(numberOfVisibleDrafts < myCreations.data.length || myCreations.hasNextPage) && (
              <Grid.Row className="text-center my-5">
                <Grid.Col xs={12}>
                  <Button
                    variant="primary"
                    onClick={async () => {
                      if (numberOfVisibleDrafts + 8 > myCreations.data.length) {
                        await myCreations.loadMore();
                      }
                      setNumberOfVisibleDrafts(value => value + 8);
                    }}
                  >
                    Load More Creations
                  </Button>
                </Grid.Col>
              </Grid.Row>
            )}
          </>
        )}
      </div>
    </EditorLayout>
  );
};

DraftList.propTypes = {
  item: PropTypes.object,
  product: PropTypes.object,
  template: PropTypes.object,
  productIds: PropTypes.array,
  children: PropTypes.node,
};

export default DraftList;
